'use client';

// СТОРОЖ КЛИЕНТА (владелец 25.08.2026). Ловит РОВНО те три жалобы, которые до сих пор
// нечем было разобрать — логов про них нет ни у Traefik, ни у веба:
//   1) «жму нижнюю вкладку — кнопка переключилась, содержимое приходит секунд через пять»
//      → нет nav.commit за 2 секунды после nav.tap = client_slow_nav;
//   2) «после выкатки приложение виснет совсем, тапы не работают» → главный поток стоит
//      дольше 3 секунд при видимой странице = client_freeze;
//   3) «долго не открывается вовсе» → оболочка не готова за 8 секунд = client_slow_boot.
// Все три — ТИХИЕ виды центра ошибок: они пишутся с полным контекстом, но владельца
// ночью не будят (QUIET_KINDS в error-center). К каждому прикладывается журнал
// (client-journal): в инциденте лежит короткий снимок и ссылка journalId на всю ленту.
//
// Кроме трёх находок сторож ведёт СБОР для их разбора: длинные задачи главного потока
// (PerformanceObserver 'longtask' — длительность и attribution) ложатся в журнал, и у
// зависания появляется виновник, а не одна цифра «экран не отвечал 6864 мс» (01.09.2026).
//
// Сторож НИЧЕГО не чинит и не меняет поведение страницы — только смотрит и докладывает.
import {
  capAllow,
  compactSnapshot,
  emptyCap,
  log,
  navKey,
  noteNavCommit,
  noteNavTap,
  tapTime,
  type WindowCap,
} from './client-journal';
import { report, sendJournal, watchAllowed } from './client-report';

/** Переход считается медленным, если содержимое не встало за это время. */
export const SLOW_NAV_MS = 2000;
/** Разрыв между кадрами при ВИДИМОЙ странице — зависание. */
export const FREEZE_MS = 3000;
/** Оболочка обязана подняться за это время от старта загрузки. */
export const SLOW_BOOT_MS = 8000;
/** Длинная задача главного потока: короче этого в журнал не пишем (шум). */
export const LONGTASK_MS = 200;
/** Сколько длинных задач пишем в минуту: у зависания их бывает пачка, лента не резиновая. */
const LONGTASK_LIMIT = 20;
const LONGTASK_WINDOW_MS = 60_000;

let started = false;
let shellReady = false;
let bootTimer: number | null = null;
/** Незакрытые тапы: адрес → таймер сторожа. */
const navTimers = new Map<string, number>();
/** Адреса, по которым сторож уже доложил, — ждём фактическую длительность. */
const slowPending = new Map<string, number>();
/** Страница уходила в фон с последнего кадра: такой разрыв зависанием НЕ считается. */
// Страница могла открыться уже свёрнутой (фоновая вкладка) — тогда события «скрылась» не будет,
// а первый кадр после показа принесёт разрыв в минуты (владелец 26.08: «зависание 945 938 мс»
// на свёрнутой вкладке Mac). Считаем стартовое состояние и любую смену видимости поводом
// пропустить следующий кадр.
let sawHidden = typeof document !== 'undefined' && document.visibilityState !== 'visible';
/** С последнего тапа по вкладке экран прятали (свернули приложение, ушли в другую вкладку):
 *  таймер сторожа в фоне идёт как попало, и «не показан за 2 секунды» тут неправда.
 *  Отдельный признак, а не sawHidden: тот гасится каждым кадром часов главного потока. */
let navHidden = false;
/** Кап длинных задач (fixed window, как у fetch в журнале). */
let longCap: WindowCap = emptyCap();

const nowMs = (): number =>
  typeof performance !== 'undefined' ? Math.round(performance.now()) : Date.now();

/**
 * Один доклад сторожа: ОДНИМ запросом уезжает и журнал, и инцидент со ссылкой на него
 * (`incident` в теле `/api/cabinet/ui-journal`).
 *
 * КАПКАН (живая проба 25.08.2026): сначала было два запроса — журнал, а инцидент в
 * `.then()` после ответа. На медленной сети (ровно та, из-за которой сторож и сработал)
 * ответ шёл 17 секунд, человек успевал уйти со страницы, и в центр ошибок не приезжало
 * НИЧЕГО. Не разносить это обратно на два запроса. Запасной путь остался: журнал не
 * доехал — инцидент уходит отдельно, лучше без ленты, чем молчание.
 */
/**
 * Страница, сборка и браузер — те же поля, что у обычного репорта (client-report.ts).
 * Без `url` отпечаток находки не знает страницы (error-center: clientPage), и ВСЕ зависания
 * ВСЕХ страниц слипались в один инцидент (f26f9ee4: 186 повторов за два месяца — мастер,
 * «Чаты», админка, лендинг — и ни одного разобрать нельзя), а первый доклад «не показан за
 * 2000 мс» и его развязка (report с url) расходились отпечатками в ДВА инцидента на одно
 * событие (b4523a9a + a0b98e14, 18.10.2026). У перехода страницей считается ЦЕЛЬ (key):
 * первый доклад уходит ещё со старой страницы, развязка — уже с новой. `build` и `ua`
 * читает дежурный по журналам (journal-guard: «после катки хуже»).
 */
function where(url: string): Record<string, unknown> {
  return {
    url,
    ua: navigator.userAgent.slice(0, 200),
    build: process.env.NEXT_PUBLIC_BUILD_ID ?? 'dev',
  };
}

function tell(kind: string, message: string, extra: Record<string, unknown>, url = window.location.pathname): void {
  if (!watchAllowed(kind)) return;
  const j = compactSnapshot();
  const context = { ...where(url), ...extra, j };
  void sendJournal(kind.replace(/^client_/, ''), {
    incident: { kind, message, context },
  }).then((id) => {
    if (!id) report(kind, message, undefined, context, false);
  });
}

/** Тап по вкладке: журналим и заводим сторожа на этот адрес. */
export function watchNavTap(from: string, to: string, src: 'native' | 'web'): void {
  if (typeof window === 'undefined') return;
  noteNavTap(from, to, src);
  // Сторож считает переходы по ПУТИ без параметров (navKey): тап приходит по ссылке
  // (`/app/messages?state=missed`), а отбой — из usePathname(), где параметров нет вовсе.
  const key = navKey(to);
  navHidden = document.visibilityState !== 'visible';
  // Новый тап снимает ВСЕХ прежних сторожей, не только своего адреса (27.08, инцидент
  // 632347c0): человек тапнул «ожидают», через 0,9 с — «пропущенные»; первый раздел
  // так и не открылся (его и не ждали), а его сторож через 2 с доложил «не показан» —
  // ложная тревога. Ждём только последний тап: остальные человек сам отменил.
  for (const timer of navTimers.values()) window.clearTimeout(timer);
  navTimers.clear();
  navTimers.set(
    key,
    window.setTimeout(() => {
      navTimers.delete(key);
      // Тап мог быть перекрыт уходом в фон — тогда «медленно» неправда. Смотрим не только
      // «сейчас скрыт», но и «прятали с момента тапа»: свернули приложение и вернулись —
      // таймер отстоял в фоне, а переход был мгновенным.
      if (navHidden || document.visibilityState !== 'visible') return;
      const ms = SLOW_NAV_MS;
      log('nav.slow', { to: key, ms });
      slowPending.set(key, nowMs());
      tell('client_slow_nav', `раздел ${key} не показан за ${ms} мс`, { to: key, ms }, key);
    }, SLOW_NAV_MS),
  );
}

/** Маршрут сменился: снимаем сторожа, дописываем фактическую длительность. */
export function watchNavCommit(to: string): void {
  if (typeof window === 'undefined') return;
  const key = navKey(to);
  const tapped = tapTime(key);
  const timer = navTimers.get(key);
  if (timer !== undefined) {
    window.clearTimeout(timer);
    navTimers.delete(key);
  }
  const ms = noteNavCommit(key);
  if (!shellReady) markShellReady();
  // Доклад уже ушёл — дописываем, сколько на самом деле заняло (иначе в инциденте
  // навсегда осталось бы «не показан за 2000 мс» без развязки).
  if (slowPending.has(key)) {
    slowPending.delete(key);
    const real = ms ?? (tapped === undefined ? null : nowMs() - tapped);
    log('nav.slow.done', { to: key, ...(real === null ? {} : { ms: real }) });
    // ТА ЖЕ форма сообщения, что у первого доклада (числа в отпечатке нормализуются) —
    // развязка ДОПИСЫВАЕТСЯ в тот же инцидент, а не заводит второй: в контексте
    // остаётся фактическая длительность, а не вечное «не показан за 2000 мс».
    report(
      'client_slow_nav',
      `раздел ${key} не показан за ${real ?? SLOW_NAV_MS} мс`,
      undefined,
      { ...where(key), to: key, ms: real, done: 1, j: compactSnapshot() },
      false,
    );
  }
}

/** Оболочка кабинета смонтирована — «долгая загрузка» больше не считается. */
export function markShellReady(): void {
  if (shellReady) return;
  shellReady = true;
  if (bootTimer !== null && typeof window !== 'undefined') {
    window.clearTimeout(bootTimer);
    bootTimer = null;
  }
  log('shell.ready');
}

/** Шаг таймера-свидетеля часов главного потока. */
const PULSE_MS = 500;

/**
 * Часы главного потока: кадр не пришёл вовремя = поток стоял.
 *
 * ДВА СВИДЕТЕЛЯ, а не один (инцидент f26f9ee4, 179 повторов к 14.09.2026). Кадров
 * (requestAnimationFrame) нет не только у стоящего потока: браузер не рисует невидимый
 * веб-вью, а visibilityState при этом «visible» — так было у демо-сцены мастера в
 * прогретом вью и так на старте приложения, пока веб-вью ещё не на экране: журнал
 * 08.09 показывал pageshow ПОСРЕДИ «зависания» 4 с — поток жил, кадров не было. Поэтому
 * зависанием считается только разрыв кадров, который подтвердил и обычный таймер:
 * стоящий поток задерживает и его, а «нет кадров при живом потоке» — не зависание, и в
 * журнал ложится строкой freeze.frames без инцидента.
 *
 * Часы заводятся ПОСЛЕ события load: до него идёт гидрация и первая отрисовка кабинета
 * (на iPhone 2–3 с, все «render …» журнала ждут первого кадра), и это не «экран не
 * отвечал», а загрузка — за неё отвечает сторож client_slow_boot (SLOW_BOOT_MS).
 */
function startFrameClock(): void {
  if (typeof requestAnimationFrame === 'undefined') return;
  const arm = (): void => {
    let last = nowMs();
    let lastPulse = nowMs();
    /** Самая длинная пауза таймера с прошлого кадра. */
    let pulseGap = 0;
    window.setInterval(() => {
      const t = nowMs();
      pulseGap = Math.max(pulseGap, t - lastPulse);
      lastPulse = t;
    }, PULSE_MS);
    const tick = () => {
      const t = nowMs();
      const gap = t - last;
      last = t;
      if (gap > FREEZE_MS && !sawHidden && document.visibilityState === 'visible') {
        // Таймер либо уже отстоял ту же паузу (сработал первым после разрыва), либо ещё
        // не сработал с её начала — в обоих случаях поток стоял по-настоящему.
        const stalled = Math.max(pulseGap, t - lastPulse) > FREEZE_MS - PULSE_MS;
        if (stalled) {
          log('freeze', { ms: gap });
          tell('client_freeze', `экран не отвечал ${gap} мс`, { ms: gap });
        } else {
          log('freeze.frames', { ms: gap });
        }
      }
      pulseGap = 0;
      sawHidden = false;
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };
  if (document.readyState === 'complete') arm();
  else window.addEventListener('load', arm, { once: true });
}

/** Кусок attribution длинной задачи (Long Tasks API). Полей мало и они не личные. */
interface TaskAttribution {
  containerType?: string;
  containerName?: string;
  containerId?: string;
  containerSrc?: string;
}

/**
 * ДЛИННЫЕ ЗАДАЧИ ГЛАВНОГО ПОТОКА (01.09.2026, инцидент f26f9ee4: «Чаты» стоят 3–7 секунд
 * в покое, а виновника в журнале нет). Часы кадров видят ТОЛЬКО факт («экран не отвечал
 * 6864 мс») — кто держал поток, по ним не узнать. PerformanceObserver отдаёт длительность
 * и attribution каждой задачи длиннее 50 мс; мы кладём в журнал те, что длиннее
 * LONGTASK_MS, и в снимке рядом с зависанием видно, что именно считало.
 *
 * Наблюдатель НИЧЕГО не меняет: он только читает. Типа longtask может не быть вовсе
 * (Safari/WKWebView) — тогда observe ничего не даст или отобьётся, и сторож живёт как жил:
 * там виновника ищем замерами noteTask (разбор ответа и перерисовка данных).
 */
function startLongTasks(): void {
  if (typeof PerformanceObserver === 'undefined') return;
  try {
    const obs = new PerformanceObserver((list) => {
      for (const e of list.getEntries()) {
        const ms = Math.round(e.duration);
        if (ms < LONGTASK_MS) continue;
        const r = capAllow(longCap, Date.now(), LONGTASK_LIMIT, LONGTASK_WINDOW_MS);
        longCap = r.state;
        if (r.flushed > 0) log('longtask.skipped', { n: r.flushed });
        if (!r.allow) continue;
        const att = (e as PerformanceEntry & { attribution?: TaskAttribution[] }).attribution ?? [];
        const a = att[0];
        log('longtask', {
          ms,
          ...(e.name && e.name !== 'unknown' ? { n: e.name } : {}),
          ...(a?.containerType ? { c: a.containerType } : {}),
          // Имя/идентификатор рамки — наши собственные, не пользовательские данные.
          ...(a?.containerName ? { cn: String(a.containerName).slice(0, 60) } : {}),
          ...(a?.containerId ? { ci: String(a.containerId).slice(0, 60) } : {}),
        });
      }
    });
    obs.observe({ type: 'longtask', buffered: true });
  } catch {
    /* типа longtask в браузере нет — наблюдателя просто не будет */
  }
}

/** Поднять сторожа. Идемпотентно; зовётся модульным кодом pwa-updater вместе с журналом. */
export function startWatch(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  try {
    document.addEventListener('visibilitychange', () => {
      sawHidden = true; // и «скрылась», и «показалась»: разрыв кадров вокруг смены видимости — не зависание
      navHidden = true; // и переход, переживший уход в фон, «медленным» не считается
    });
    // Демо-сцена мастера (/wizard-scene/*) живёт в невидимом веб-вью, который приложение
    // прогревает заранее (wizWarm), а браузер невидимому вью кадры не даёт — часы главного
    // потока видели там «зависание 9 с» 170 раз за две недели при живой странице
    // (инцидент f26f9ee4, 10.09.2026). Сцена — картинка без нажатий, замирать ей нечему.
    if (!/\/wizard-scene\//.test(location.pathname)) startFrameClock();
    startLongTasks();
    bootTimer = window.setTimeout(() => {
      bootTimer = null;
      if (shellReady || document.visibilityState !== 'visible') return;
      // Кабинет живёт под /app: на лендинге и публичных страницах оболочки нет вовсе,
      // и «оболочка не поднялась» там ничего не значит.
      if (!/\/app(\/|$)/.test(location.pathname)) return;
      log('boot.slow', { ms: SLOW_BOOT_MS });
      tell('client_slow_boot', `кабинет не открылся за ${SLOW_BOOT_MS} мс`, { ms: SLOW_BOOT_MS });
    }, SLOW_BOOT_MS);
  } catch {
    /* сторож — диагностика: его поломка не имеет права трогать страницу */
  }
}

/** Только для тестов. */
export function resetWatchForTests(): void {
  started = false;
  shellReady = false;
  sawHidden = false;
  navHidden = false;
  longCap = emptyCap();
  navTimers.clear();
  slowPending.clear();
  if (bootTimer !== null && typeof window !== 'undefined') window.clearTimeout(bootTimer);
  bootTimer = null;
}
